import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Services from './components/Services';
import Philosophy from './components/Philosophy';
import SuccessStories from './components/SuccessStories';
import About from './components/About';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollSpy from './components/ScrollSpy';
import BackToTop from './components/BackToTop';
import LanguageSelector from './components/LanguageSelector';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';

const Navigation: React.FC = () => {
  const { t } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const element = document.getElementById('home');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="nav">
      <a href="/" className="logo" onClick={handleLogoClick}>NutriFit Consulting</a>
      <div className="nav-links">
        {location.pathname === '/' && (
          <>
            <a href="#services" onClick={(e) => handleNavClick(e, 'services')}>{t('nav.services')}</a>
            <a href="#philosophy" onClick={(e) => handleNavClick(e, 'philosophy')}>{t('nav.philosophy')}</a>
            <a href="#success-stories" onClick={(e) => handleNavClick(e, 'success-stories')}>{t('nav.successStories')}</a>
            <a href="#about" onClick={(e) => handleNavClick(e, 'about')}>{t('nav.about')}</a>
            <a href="#contact" onClick={(e) => handleNavClick(e, 'contact')}>{t('nav.contact')}</a>
          </>
        )}
        {isAuthenticated ? (
          <>
            <Link to="/dashboard" className="nav-link">{t('dashboard.title') || 'Dashboard'}</Link>
            <button onClick={logout} className="nav-button">{t('auth.logout')}</button>
          </>
        ) : (
          <>
            <Link to="/login" className="nav-link">{t('auth.login')}</Link>
            <Link to="/register" className="nav-link">{t('auth.register')}</Link>
          </>
        )}
        <LanguageSelector />
      </div>
      <button 
        className={`hamburger-menu ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={() => setIsMobileMenuOpen(false)}></div>
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        {location.pathname === '/' && (
          <>
            <a href="#services" onClick={(e) => { handleNavClick(e, 'services'); setIsMobileMenuOpen(false); }}>{t('nav.services')}</a>
            <a href="#philosophy" onClick={(e) => { handleNavClick(e, 'philosophy'); setIsMobileMenuOpen(false); }}>{t('nav.philosophy')}</a>
            <a href="#success-stories" onClick={(e) => { handleNavClick(e, 'success-stories'); setIsMobileMenuOpen(false); }}>{t('nav.successStories')}</a>
            <a href="#about" onClick={(e) => { handleNavClick(e, 'about'); setIsMobileMenuOpen(false); }}>{t('nav.about')}</a>
            <a href="#contact" onClick={(e) => { handleNavClick(e, 'contact'); setIsMobileMenuOpen(false); }}>{t('nav.contact')}</a>
          </>
        )}
        {isAuthenticated ? (
          <>
            <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">{t('dashboard.title') || 'Dashboard'}</Link>
            <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="mobile-menu-button">{t('auth.logout')}</button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">{t('auth.login')}</Link>
            <Link to="/register" onClick={() => setIsMobileMenuOpen(false)} className="mobile-menu-link">{t('auth.register')}</Link>
          </>
        )}
        <div className="mobile-menu-language">
          <LanguageSelector />
        </div>
      </div>
    </nav>
  );
};

const HomePage: React.FC = () => {
  return (
    <ScrollSpy>
      <div className="App">
        <Header />
        <Services />
        <Philosophy />
        <SuccessStories />
        <About />
        <Contact />
        <Footer />
        <BackToTop />
      </div>
    </ScrollSpy>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>;
  }

  return isAuthenticated ? children : null;
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <Navigation />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
